import {
  ORDER_API_URL,
  BASE_SCENE_ID,
  PRODUCT_ID,
  APP_URL,
  SNAPSHOT_CAMERAS,
  CAMERA_TARGET,
} from '../constants';
import BlockLayout from '../utils/BlockLayout';

/**
 * this file contains all the method related to order
 */

/**
 * public api
 * post the order to mythreekit backend
 */
const postOrder = () => (customer, metadata) => {
  const attrs = {
    customer,
    metadata: {
      ...metadata,
      configuration: createConfigurationOrderData(metadata.configuration),
    },
    sceneId: BASE_SCENE_ID,
    productId: PRODUCT_ID,
  };
  return fetch(ORDER_API_URL, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(attrs),
  });
};

// because the app use custom configurator, this configurion will
// convert necessary data into a compatible format
function createConfigurationOrderData(configuration) {
  return Object.entries(configuration).reduce(
    (configOrderData, [attrName, attrValue]) => {
      configOrderData[attrName] = { ...attrValue };
      if (attrValue.value) {
        if (attrValue.blocks) {
          configOrderData[attrName].blocks = { ...attrValue.blocks };
          Object.entries(configOrderData[attrName].blocks).forEach(
            ([blockName, block]) =>
              (configOrderData[attrName].blocks[blockName] = block.toJson())
          );
        }
      } else {
        Object.entries(attrValue).forEach(([itemName, itemValues]) => {
          configOrderData[attrName][itemName] = [...itemValues];
          itemValues.forEach((itemValue, idx) => {
            if (itemValue.blocks) {
              configOrderData[attrName][itemName][idx] = { ...itemValue };
              configOrderData[attrName][itemName][idx].blocks = {
                ...itemValue.blocks,
              };
              Object.entries(itemValue.blocks).forEach(
                ([blockName, block]) =>
                  (configOrderData[attrName][itemName][idx].blocks[
                    blockName
                  ] = block.toJson())
              );
            }
          });
        });
      }
      return configOrderData;
    },
    {}
  );
}

/**
 * public api
 */
const fetchOrderFromHash = () => (hash) => {
  return new Promise((resolve, reject) => {
    fetch(`${APP_URL}/order/orderFromHash/${hash}`)
      .then((res) => {
        if (res.status !== 200) reject('fetch order error!');
        return res.json();
      })
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

const fetchOrder = () => (orderId, token, org) => {
  return new Promise((resolve, reject) => {
    fetch(`${ORDER_API_URL}/${orderId}?token=${token}&org=${org}`)
      .then((res) => {
        if (res.status !== 200) reject('fetch order error!');
        return res.json();
      })
      .then((data) => resolve(data))
      .catch((err) => reject(err));
  });
};

/**
 * public api
 * It will import and position the selected model
 * resume app configuration and models state from order data
 */

const resumeStateFromOrder = (api) => async (orderData, importModel) => {
  const { models, configuration: orderConfig } = orderData.metadata;
  if (!models || !orderConfig) return Promise.reject();
  const baseModelId = orderConfig.base.modelId;
  if (!baseModelId) return Promise.reject();
  const configuration = resumeConfigurationBlockData(
    orderConfig,
    api.deps.THREE
  );
  if (configuration.colorPremiumPaint) {
    if (configuration.colorPremiumPaint.value) {
      configuration.colorStandardPaint.value =
        configuration.colorPremiumPaint.value;
    }
    delete configuration.colorPremiumPaint;
  }
  const nodeMap = await resumeModelRecursively(
    api,
    baseModelId,
    configuration,
    models,
    importModel
  );
  updateConfigurationModelId(nodeMap, configuration);
  updateModelsModelId(nodeMap, models);
  return { configuration, models };
};

// resume the blocklayout class from block json
function resumeConfigurationBlockData(orderConfig, THREE) {
  return Object.entries(orderConfig).reduce(
    (configuration, [attrName, attrValue]) => {
      configuration[attrName] = { ...attrValue };
      if (attrValue.value) {
        if (attrValue.blocks)
          Object.entries(configuration[attrName].blocks).forEach(
            ([blockName, blockJson]) => {
              const block = new BlockLayout(THREE);
              block.fromJson(blockJson);
              configuration[attrName].blocks[blockName] = block;
            }
          );
      } else {
        Object.values(attrValue).forEach((itemValues) =>
          itemValues.forEach((itemValue) => {
            if (itemValue.blocks)
              Object.entries(itemValue.blocks).forEach(
                ([blockName, blockJson]) => {
                  const block = new BlockLayout(THREE);
                  block.fromJson(blockJson);
                  itemValue.blocks[blockName] = block;
                }
              );
          })
        );
      }
      return configuration;
    },
    {}
  );
}

// this function recursively import and resume all the models
// it returns a nodeMap, that each key is the old modelId from orderData
// and each value is the corresponding new modelId after import
async function resumeModelRecursively(
  api,
  modelId,
  configuration,
  models,
  importModel,
  parentId
) {
  let transform;
  if (parentId) {
    const blocks = getModelBlocksData(parentId, configuration, models);
    const block = Object.values(blocks).find((block) =>
      block.containNode(modelId)
    );
    transform = block.getInsertTransform(block.getNodeInsertion(modelId));
  }
  const { sceneId, children } = models[modelId];
  const importData = await importModel(sceneId, { transform });
  return Promise.all(
    children.map((childId) =>
      resumeModelRecursively(
        api,
        childId,
        configuration,
        models,
        importModel,
        modelId
      )
    )
  ).then((nodeMapArray) => {
    const nodeMap = [
      ...nodeMapArray,
      { [modelId]: importData.modelId },
    ].reduce((nodeMap, subMap) => Object.assign(nodeMap, subMap));
    api.sceneGraph.reparent(
      importData.modelId,
      children.map((childId) => nodeMap[childId])
    );
    return Promise.resolve(nodeMap);
  });
}

function getModelBlocksData(modelId, configuration, models) {
  const { attribute, value } = models[modelId];
  if (configuration[attribute].value) return configuration[attribute].blocks;
  return configuration[attribute][value].find(
    (data) => data.modelId === modelId
  ).blocks;
}

function updateConfigurationModelId(nodeMap, configuration) {
  Object.values(configuration).forEach((attrValue) => {
    if (attrValue.value) {
      if (attrValue.modelId) attrValue.modelId = nodeMap[attrValue.modelId];
      if (attrValue.blocks)
        Object.values(attrValue.blocks).forEach((block) =>
          block.updateNodeId(nodeMap)
        );
    } else {
      Object.values(attrValue).forEach((itemValues) =>
        itemValues.forEach((itemValue) => {
          itemValue.modelId = nodeMap[itemValue.modelId];
          if (itemValue.blocks)
            Object.values(itemValue.blocks).forEach((block) =>
              block.updateNodeId(nodeMap)
            );
        })
      );
    }
  });
}

function updateModelsModelId(nodeMap, models) {
  Object.entries(models).forEach(([modelId, modelData]) => {
    modelData.parent = nodeMap[modelData.parent];
    modelData.children = modelData.children.map((childId) => nodeMap[childId]);
    models[nodeMap[modelId]] = modelData;
    delete models[modelId];
  });
}

const takeSnapshot = (api) => {
  api.commands.setCommandOptions('snapshot', {
    dataType: 'blob',
    width: 1024,
    height: 1024,
  });
  return (options) => {
    const { size } = options || {};
    const date = new Date().toUTCString();
    const snapshotViews = Object.keys(SNAPSHOT_CAMERAS);
    const query = {
      name: CAMERA_TARGET,
      plug: 'Transform',
      property: 'translation',
    };
    const sceneBB = api.scene.getNodeBoundingBox(api.scene.find('Objects'));
    const translation = api.scene.get(query);
    translation.y = sceneBB.getCenter().y;
    api.scene.set(query, translation);
    return Promise.all(
      snapshotViews.map((view) => {
        const cameraName = SNAPSHOT_CAMERAS[view];
        const cameraId = api.scene.find(cameraName);
        api.commands.setCommandOptions('snapshot', { camera: cameraName });
        api.player.frameScene(undefined, {
          cameraId,
          mode: 'geometry',
          padding: 0.1,
        });
        const snapshot = api.commands.runCommand('snapshot');
        const form = new FormData();
        const fileName = `BOSTONtec_${view}_${date}.png`;
        form.append('file', snapshot);
        form.append('name', fileName);
        form.append('targetFormat', 'png');
        form.append('resize', !!size ? true : false);
        if (size) form.append('maxSize', size);
        return fetch('https://clara.io/api/files/image', {
          method: 'POST',
          body: form,
        })
          .then((res) => res.json())
          .then(
            (imageData) =>
              `https://clara.io/resources/${imageData.hash}?filename=${fileName}`
          );
      })
    ).then((snapshotUrls) =>
      snapshotUrls.reduce(
        (obj, url, idx) => Object.assign(obj, { [snapshotViews[idx]]: url }),
        {}
      )
    );
  };
};

const orderApiCreater = (api) => ({
  postOrder: postOrder(),
  fetchOrderFromHash: fetchOrderFromHash(),
  fetchOrder: fetchOrder(),
  resumeStateFromOrder: resumeStateFromOrder(api),
  takeSnapshot: takeSnapshot(api),
});

export default orderApiCreater;



// WEBPACK FOOTER //
// ./src/modules/order.js